import React, { useState, useMemo } from "react";
import { IoMdEyeOff } from "react-icons/io";
import { IoEye } from "react-icons/io5";
import { MdLock } from "react-icons/md";
import { FiCheck, FiX } from "react-icons/fi";
import { evaluatePasswordRules } from "../../utils/passwordPolicy";

/**
 * PasswordInput
 *
 * Backward compatible: existing usages (login form, change-password form)
 * keep working unchanged because all new props are optional.
 *
 * Optional new props for signup flows:
 *   showStrength      — render the strength meter + per-rule checklist below the field
 *   rulesContext      — { email, name } so identity-containing passwords are flagged
 *   autoComplete      — pass "new-password" on signup so password managers prompt
 *                       to save instead of trying to autofill an existing password
 */
const PasswordInput = ({
  id,
  label,
  name,
  value,
  onChange,
  showStrength = false,
  rulesContext,
  autoComplete = "current-password",
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState(value);

  const handleInputChange = (e) => {
    // e.target is always defined for input change events — the prior
    // `e?.target` shape would have thrown a TypeError on the destructure
    // if e were ever undefined. Use a plain access and guard.
    if (!e || !e.target) return;
    const { value } = e.target;
    setInputValue(value);
    if (onChange) onChange(value);
  };

  // Compute rules only when needed — saves a render pass on the login form
  // where `showStrength` is false.
  const rules = useMemo(() => {
    if (!showStrength) return null;
    return evaluatePasswordRules(value ?? inputValue ?? "", rulesContext || {});
  }, [showStrength, value, inputValue, rulesContext]);

  const passedCount = rules ? rules.filter((r) => r.passed).length : 0;
  const totalRules = rules ? rules.length : 0;
  const strengthPct = totalRules ? (passedCount / totalRules) * 100 : 0;

  // Strength meter color: red < 60%, amber 60–99%, green at 100%
  const meterColor =
    strengthPct === 100
      ? "bg-emerald-500"
      : strengthPct >= 60
        ? "bg-amber-400"
        : strengthPct > 0
          ? "bg-red-500"
          : "bg-gray-700";

  const meterLabel =
    strengthPct === 100
      ? "Strong"
      : strengthPct >= 60
        ? "Almost there"
        : strengthPct > 0
          ? "Weak"
          : "";

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-300 mb-2"
      >
        {label || "Password"}
      </label>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MdLock size={18} className="text-gray-400" />
        </div>
        <input
          id={id}
          name={name}
          type={showPassword ? "text" : "password"}
          autoComplete={autoComplete}
          value={inputValue}
          onChange={handleInputChange}
          className="pl-10 pr-10 block w-full rounded-lg py-2.5 bg-[var(--lighter-dark)] border border-white/10 placeholder-gray-500 text-white focus:outline-none focus:border-[#1ec8c8]/60 focus:ring-2 focus:ring-[#1ec8c8]/30 transition-colors"
          placeholder="••••••••"
        />
        <div
          onClick={() => setShowPassword(!showPassword)}
          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white cursor-pointer"
        >
          {showPassword ? <IoMdEyeOff size={18} /> : <IoEye size={18} />}
        </div>
      </div>

      {showStrength && rules && (
        <div className="mt-2 space-y-2">
          {/* Strength meter */}
          {(value || inputValue) ? (
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 rounded-full bg-gray-700 overflow-hidden">
                <div
                  className={`h-full transition-all duration-200 ${meterColor}`}
                  style={{ width: `${strengthPct}%` }}
                />
              </div>
              <span
                className={`text-xs font-medium min-w-[80px] text-right ${
                  strengthPct === 100
                    ? "text-emerald-400"
                    : strengthPct >= 60
                      ? "text-amber-400"
                      : "text-red-400"
                }`}
              >
                {meterLabel}
              </span>
            </div>
          ) : null}

          {/* Per-rule checklist */}
          <ul className="space-y-1">
            {rules.map((rule) => (
              <li
                key={rule.id}
                className={`flex items-start gap-1.5 text-xs ${
                  rule.passed ? "text-emerald-400" : "text-gray-400"
                }`}
              >
                {rule.passed ? (
                  <FiCheck size={14} className="flex-shrink-0 mt-0.5" />
                ) : (
                  <FiX size={14} className="flex-shrink-0 mt-0.5 text-gray-500" />
                )}
                <span>{rule.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PasswordInput;
